import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';

// Mirrors the backend's own INLINE_RENDERABLE_MIME_TYPES allowlist
// (students.controller.ts previewDocument) — anything else is already
// forced to attachment disposition server-side, so there's nothing to
// render inline for it here either.
const INLINE_RENDERABLE_MIME_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

// `fetchBlob` callers either throw the raw AxiosError (students-documents
// path) or a plain Error whose message is already the best available text
// (payments' getPdfBlob already re-reads a Blob error body as JSON) — the
// plain-Error case is shown verbatim rather than remapped, since it's
// already more specific than a generic per-status guess.
const describePreviewError = (err: unknown): string => {
  if (isAxiosError(err)) {
    switch (err.response?.status) {
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'You do not have permission to view this document.';
      case 404:
        return 'This document could not be found. It may have been removed.';
      case 410:
        return 'This document is no longer available.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      default:
        return 'Unable to open this document. Please try again or contact the administrator.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Unable to open this document. Please try again or contact the administrator.';
};

export interface DocumentPreviewTarget {
  /** Unique per open — used as the fetch effect's dependency, not
   * displayed. A documentId/paymentId works, but doesn't have to. */
  key: string | number;
  originalName: string;
  mimeType: string;
  /** Fetches (or re-fetches, on retry) the file's bytes. Never called more
   * than once per `key` unless the dialog is reopened. */
  fetchBlob: () => Promise<Blob>;
  /** Bundled here (rather than a separate top-level prop) so the caller
   * doesn't have to recover which row is open just to wire the Download
   * button — each target already knows how to download itself. */
  onDownload: () => void;
}

/**
 * Generated-agreement blank-preview investigation (2026-09-28) — root
 * cause: the previous "View" action fetched the document as a Blob (the
 * only way to attach the Authorization header this app authenticates
 * with — a plain <iframe>/<a> pointed straight at the API URL would never
 * carry it) and then called `window.open(URL.createObjectURL(blob),
 * '_blank')`. Chromium reliably fails to navigate a *new* top-level
 * browsing context to a blob: URL created by a different one — confirmed
 * via a headless-browser repro where the opened tab's URL and document
 * both come back completely empty — producing exactly the reported
 * blank page. Rendering that same already-fetched blob inline via an
 * <iframe>/<img> in THIS document (a modal, never a new tab/window) is
 * the standard, reliable fix: no new PDF library needed, since the
 * browser's own built-in PDF viewer handles an <iframe src="blob:...">
 * within the same tab correctly.
 */
export const DocumentPreviewDialog = ({
  target,
  onClose,
}: {
  target: DocumentPreviewTarget | null;
  onClose: () => void;
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlobUrl(null);

    target
      .fetchBlob()
      .then((blob) => {
        if (cancelled) return;
        setBlobUrl(URL.createObjectURL(blob));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describePreviewError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.key]);

  useEffect(() => {
    // Revoke only when replaced by a new preview or the dialog unmounts —
    // never on the same render that just created the URL, or the
    // <iframe>/<img> below would lose it before it can load.
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const canPreviewInline = target ? INLINE_RENDERABLE_MIME_TYPES.has(target.mimeType) : false;

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography component="span" variant="subtitle1" noWrap>
          {target?.originalName}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {target && (
            <IconButton onClick={target.onDownload} aria-label="Download">
              <DownloadIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Box>
        )}
        {!loading && !error && blobUrl && target && (
          canPreviewInline ? (
            target.mimeType === 'application/pdf' ? (
              <Box component="iframe" src={blobUrl} title={target.originalName} sx={{ flex: 1, border: 0 }} />
            ) : (
              <Box
                component="img"
                src={blobUrl}
                alt={target.originalName}
                sx={{ flex: 1, objectFit: 'contain', width: '100%', bgcolor: 'action.hover' }}
              />
            )
          ) : (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, gap: 2 }}>
              <Typography color="text.secondary">This file type can&apos;t be previewed inline.</Typography>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={target.onDownload}>
                Download instead
              </Button>
            </Box>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};
